import { useEffect, useMemo, useRef, useState } from "react";
import CustomModal from "../CustomModal/CustomModal";
import Input from "../Input/Input";
import Select from "../Select/Select";
import Button from "../Button/Button";
import Skeleton from "../Skeleton/Skeleton";
import useLoginAs from "../../../hooks/useLoginAs";
import useDebounce from "../../../hooks/useDebounce";
import { getUsers } from "../../../services/userService";
import {
  normalizeUser,
  NormalizedUser,
  RawUser,
} from "../../../hooks/useGetUsers";
import SearchIcon from "Assets/Img/SearchIcon";
import "./SwitchRoleModal.scss";

interface SwitchRoleModalProps {
  show: boolean;
  onClose: () => void;
}

const LIMIT = 10;
const SKELETON_ROWS = Array.from({ length: 6 }, (_, i) => i);
const ROLE_DISPLAY: Record<string, string> = {
  GC: "General Contractor",
};

const ROLE_FILTER_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "inspector", label: "Inspector" },
  { value: "owner", label: "Owner / Borrower" },
  { value: "lender", label: "Lender / Bank" },
  { value: "gc", label: "General Contractor" },
];

const SwitchRoleModal = ({ show, onClose }: SwitchRoleModalProps) => {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);

  const [users, setUsers] = useState<NormalizedUser[]>([]);
  const [usersPage, setUsersPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { loginAs, loading: switching, error } = useLoginAs();
  const [switchingKey, setSwitchingKey] = useState<string | null>(null);
  const excludeRoles = useMemo(() => ["Architect", "Admin"], []);
  // One row per switchable role, so a multi-role user (e.g. Admin + Inspector + Owner)
  // is listed once for each role they can be switched into.
  const switchableUsers = useMemo(
    () =>
      users.flatMap((user) =>
        (user.roles.length ? user.roles : [user.role])
          .filter(
            (role) =>
              role &&
              !excludeRoles.includes(role) &&
              (!roleFilter || role.toLowerCase() === roleFilter),
          )
          .map((role) => ({ user, role })),
      ),
    [users, excludeRoles, roleFilter],
  );

  const loadUsersPageImpl = async (pageNum: number, reset: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoadingUsers(true);
    try {
      const params: Record<string, unknown> = { page: pageNum, limit: LIMIT };
      if (roleFilter) params.role = roleFilter;
      if (debouncedSearch) params.q = debouncedSearch;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await getUsers(params);
      const data: RawUser[] = Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      const totalPages: number = res?.data?.pagination?.totalPages ?? 1;
      const normalized = data.map(normalizeUser);
      setUsers((prev) => (reset ? normalized : [...prev, ...normalized]));
      setHasMore(pageNum < totalPages);
      setUsersPage(pageNum);
    } catch {
    } finally {
      fetchingRef.current = false;
      if (reset) setLoadingUsers(false);
    }
  };
  // Kept fresh every render so effects below always call the version
  // closed over the latest roleFilter/debouncedSearch, instead of the
  // stale values captured by a one-time useRef initializer.
  const loadUsersPageRef = useRef(loadUsersPageImpl);
  loadUsersPageRef.current = loadUsersPageImpl;
  const loadUsersPage = (pageNum: number, reset: boolean) =>
    loadUsersPageRef.current(pageNum, reset);

  // Reload whenever the modal opens (and when the filters change) so the list is never stale.
  useEffect(() => {
    if (!show) return;
    setUsers([]);
    setHasMore(false);
    loadUsersPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, roleFilter, debouncedSearch]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingRef.current) {
          loadUsersPage(usersPage + 1, false);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, usersPage]);

  const handleSwitch = async (key: string, userId: string, role: string) => {
    setSwitchingKey(key);
    await loginAs(userId, role.toLowerCase());
    setSwitchingKey(null);
  };

  return (
    <CustomModal
      show={show}
      handleClose={onClose}
      title="Switch Role"
      isFooterVisible={false}
      className="switchRoleModalDialog"
    >
      <div className="switchRoleModal">
        <div className="srmFilters">
          <div className="srmSearch">
            <Input
              icon={<SearchIcon />}
              placeholder="Search users by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select
            className="srmRoleFilter"
            options={ROLE_FILTER_OPTIONS}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            height="38px"
          />
        </div>

        {error && <div className="srmError">{error}</div>}

        <div className="srmList">
          {loadingUsers ? (
            SKELETON_ROWS.map((i) => (
              <div className="srmRow skeletonPadding" key={i}>
                <Skeleton width={36} height={36} borderRadius="50%" />
                <div className="srmInfo">
                  <Skeleton
                    width="50%"
                    height={13}
                    style={{ marginBottom: 6 }}
                  />
                  <Skeleton width="70%" height={11} />
                </div>
                <Skeleton width={90} height={12} />
                <Skeleton width={72} height={30} borderRadius={6} />
              </div>
            ))
          ) : switchableUsers.length === 0 ? (
            <div className="srmEmpty">No users found.</div>
          ) : (
            <>
              {switchableUsers.map(({ user, role }) => (
                <div className="srmRow" key={`${user.id}-${role}`}>
                  <div
                    className="srmAvatar"
                    style={{ backgroundColor: user.color }}
                  >
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.firstName} />
                    ) : (
                      user.initials
                    )}
                  </div>
                  <div className="srmInfo">
                    <div className="srmName">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="srmEmail">{user.email}</div>
                    <span className="srmRole">
                      <span className="srmRoleDot" />
                      {ROLE_DISPLAY[role] ?? role}
                    </span>
                  </div>

                  <Button
                    className="btn btnPrimary btnSm"
                    onClick={() =>
                      handleSwitch(`${user.id}-${role}`, user.id, role)
                    }
                    disabled={switching}
                  >
                    {switching && switchingKey === `${user.id}-${role}`
                      ? "Switching…"
                      : "Switch to this user"}
                  </Button>
                </div>
              ))}
              {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default SwitchRoleModal;
